import cn from 'classnames';
import { useRef } from 'react';

import { useDictionary } from 'src/hooks/useDictionary';

import { setJumpLinkAttributes } from 'src/utils/jumpLinks';

import ArrowRight from 'assets/icons/arrow-forward.svg';

import RichText from 'components/BaseHelpers/RichText';
import Text from 'components/BaseHelpers/Text';
import VideoPlayer, { type VideoPlayerHandle } from 'components/BaseHelpers/VideoPlayer';

import { ComponentProps } from '../../../types/component-props';
import { CoreVideoGridData } from '../../../types/components';

import styles from './CoreVideoGrid.module.scss';

export type CoreVideoGridProps = ComponentProps & CoreVideoGridData;

const CoreVideoGrid = (props: CoreVideoGridProps): JSX.Element => {
  const { heading, description, videos, jumpLinkLabel } = props.fields;
  const dictionary = useDictionary();
  const videoPlayerRefs = useRef<Record<string, VideoPlayerHandle | null>>({});
  const playingVideoKeyRef = useRef<string | null>(null);

  const handlePlay = (videoKey: string) => {
    const currentVideoKey = playingVideoKeyRef.current;

    if (currentVideoKey && currentVideoKey !== videoKey) {
      videoPlayerRefs.current[currentVideoKey]?.pause();
    }

    playingVideoKeyRef.current = videoKey;
  };

  if (videos.length === 0) {
    return <></>;
  }

  return (
    <div className={cn(styles.main, 'container-10')} {...setJumpLinkAttributes(jumpLinkLabel)}>
      <Text tag="h2" className={cn('heading-h3', styles.heading)} field={heading} />
      <RichText className="body-med" field={description} />
      <ul className={cn(styles.list)}>
        {videos.map((item, index) => {
          const videoKey = `${item.platform}-${item.videoCode}-${index}`;

          return (
            <li key={videoKey} className={styles['list-item']}>
              <VideoPlayer
                ref={(videoPlayer) => {
                  videoPlayerRefs.current[videoKey] = videoPlayer;
                }}
                videoData={item.videoCode}
                videoFile={item.videoFile}
                thumbnailImage={item.thumbnail}
                platform={item.platform}
                onPlay={() => handlePlay(videoKey)}
              />
              <div className={styles.content}>
                <Text tag="h3" className={cn('heading-h5', styles.name)} field={item.name} />
                <RichText
                  className={cn('body-med', styles['video-description'])}
                  field={item.description}
                />
                {item.transcript.href && (
                  <a
                    href={item.transcript.href}
                    className={cn(styles.transcript, 'body-sm')}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {dictionary.viewTranscriptLabel} <ArrowRight className={styles.arrow} />
                  </a>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default CoreVideoGrid;
